import {ReactElement, ReactNode, startTransition, useEffect, useState} from "react";
import {css} from "@emotion/react";
import {skipToken} from "@reduxjs/toolkit/query";

import {CitySlug, getCityOption} from "../../../../../config/cities";
import {DistrictMapLegendItem} from "../../../../../utils/dynamic_district_map_legend_values";
import {breakpoints} from "../../../common/app/styles/breakpoints";
import {Card} from "../../../components/card/Card";
import {Locker} from "../../../components/card/components/card_lock/Locker";
import {LazyOpenStreetMap} from "../../../components/LazyOpenStreetMap";
import {useGetRegionGeometryQuery} from "../../../components/map/api/charts_regions_api";
import {IOsmDistrictPolygon} from "../../../components/map/types/osm_district_polygon";

/** Non-sticky analytical cards: bounded height. Sticky cards use `height: 100%` inside full-viewport column. */
const DISTRICT_OSM_MAP_HEIGHT_COMPACT = "clamp(300px, 44vh, 480px)";

const DISTRICT_OSM_CENTER: Partial<Record<CitySlug, [number, number]>> = {
    [CitySlug.WARSZAWA]: [52.2297, 21.0122],
    [CitySlug.KRAKOW]: [50.0647, 19.945],
    [CitySlug.WROCLAW]: [51.1079, 17.0385],
    [CitySlug.GDANSK]: [54.352, 18.6466],
    [CitySlug.POZNAN]: [52.4064, 16.9252],
    [CitySlug.LODZ]: [51.7592, 19.456],
    [CitySlug.SZCZECIN]: [53.4285, 14.5528]
};

interface DistrictOsmMapCardProps {
    citySlug: CitySlug | null;
    regionsMapSlug?: string;
    regionsMapParentId?: number;
    regionsMapChildType?: number;
    regionsMapRegionId?: number;
    /** Slug prefix for joining stats `slug_district` to region polygons (defaults to city `requestCitySlug`). */
    districtStatsCitySlug?: string;
    districtsMapAvailable?: boolean;
    districtValuesBySlug?: Record<string, number>;
    metricLabel: string;
    locale: string;
    legendColors?: string[];
    legendItems?: DistrictMapLegendItem[];
    legendType?: "quantitative" | "pricing";
    districtMetricFormat?: "default" | "percent";
    legendSize?: "default" | "large" | "xlarge";
    /** Same as other analytical `Card`s — `useRegionPermission` / dashboard map. */
    locked?: boolean;
    /** Empty autocomplete selection — same overlay as other analytical cards. */
    empty?: boolean;
    /** Region ID for fetching city outline via `useGetRegionGeometryQuery` when locked. */
    regionId?: number;
    title?: ReactElement;
    helper?: string | ReactNode;
    sticky?: boolean;
    chartId?: string;
    /** When false, render nothing (same as removed `DistrictsMapChartCard`). */
    visible?: boolean;
    districtDrillDownEnabled?: boolean;
    onDistrictPolygonClick?: (district: IOsmDistrictPolygon) => void;
    onBackToParentRegion?: () => void;
    backToParentLabel?: string;
    cityOutlineMetric?: boolean;
}

export const DistrictOsmMapCard = ({
    citySlug,
    regionsMapSlug: regionsMapSlugOverride,
    regionsMapParentId: regionsMapParentIdOverride,
    regionsMapChildType: regionsMapChildTypeOverride,
    regionsMapRegionId: regionsMapRegionIdOverride,
    districtStatsCitySlug: districtStatsCitySlugOverride,
    districtsMapAvailable,
    districtValuesBySlug,
    metricLabel,
    locale,
    legendColors,
    legendItems,
    legendType,
    districtMetricFormat,
    legendSize,
    locked,
    empty,
    regionId,
    title,
    helper,
    sticky = false,
    chartId,
    visible = true,
    districtDrillDownEnabled,
    onDistrictPolygonClick,
    onBackToParentRegion,
    backToParentLabel,
    cityOutlineMetric = false
}: DistrictOsmMapCardProps) => {
    const [isClientMounted, setIsClientMounted] = useState(false);

    const {data: regionGeometry} = useGetRegionGeometryQuery(locked && regionId != null ? regionId : skipToken);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            startTransition(() => {
                setIsClientMounted(true);
            });
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, []);

    if (!visible) {
        return null;
    }

    const isDistrictMapAvailable = Boolean(districtsMapAvailable);
    const cityOption = citySlug != null ? getCityOption(citySlug) : null;
    const hasExplicitMapParams =
        regionsMapSlugOverride != null || regionsMapParentIdOverride != null || regionsMapRegionIdOverride != null;
    const regionsMapSlug =
        regionsMapSlugOverride ??
        (!hasExplicitMapParams && isDistrictMapAvailable ? cityOption?.requestMapSlug : undefined);
    const regionsMapParentId =
        regionsMapParentIdOverride ??
        (!hasExplicitMapParams && isDistrictMapAvailable ? cityOption?.districtMapParentRegionId : undefined);
    const regionsMapRegionId = regionsMapRegionIdOverride;
    const center = (citySlug != null ? DISTRICT_OSM_CENTER[citySlug] : undefined) ?? [52.2297, 21.0122];
    const mapHeight = sticky ? "100%" : DISTRICT_OSM_MAP_HEIGHT_COMPACT;

    const mapBackAction =
        onBackToParentRegion && backToParentLabel ? (
            <button type="button" css={backButtonStyle} onClick={onBackToParentRegion}>
                {backToParentLabel}
            </button>
        ) : null;

    return (
        <Card
            area="map"
            chartPlaceholder="districtMap"
            sticky={sticky}
            title={title}
            helper={helper}
            chartId={chartId}
            afterHeaderContent={mapBackAction}
            empty={empty}
        >
            <div css={lockedMapContainer(sticky)}>
                {locked && <Locker locked isMap />}
                <div css={[mapWrapper, locked && mapWrapperLocked]}>
                    {isClientMounted ? (
                        <LazyOpenStreetMap
                            height={mapHeight}
                            center={center}
                            zoom={11}
                            regionsMapParentId={locked ? undefined : regionsMapParentId}
                            regionsMapChildType={locked ? undefined : regionsMapChildTypeOverride}
                            regionsMapRegionId={locked ? undefined : regionsMapRegionId}
                            regionsMapSlug={locked ? undefined : regionsMapSlug}
                            regionGeometry={locked ? regionGeometry : undefined}
                            districtValuesBySlug={locked ? undefined : districtValuesBySlug}
                            districtStatsCitySlug={
                                isDistrictMapAvailable && !locked
                                    ? (districtStatsCitySlugOverride ??
                                      cityOption?.requestCitySlug ??
                                      regionsMapSlugOverride ??
                                      regionsMapSlug)
                                    : undefined
                            }
                            legendColors={locked ? undefined : legendColors}
                            legendItems={locked ? undefined : legendItems}
                            legendType={locked ? undefined : legendType}
                            districtMetricFormat={locked ? undefined : districtMetricFormat}
                            legendSize={legendSize ?? "xlarge"}
                            addedMetricLabel={metricLabel}
                            locale={locale}
                            districtDrillDownEnabled={locked ? false : districtDrillDownEnabled}
                            onDistrictPolygonClick={locked ? undefined : onDistrictPolygonClick}
                            cityOutlineMetric={cityOutlineMetric}
                        />
                    ) : (
                        <div style={{height: mapHeight, width: "100%"}} aria-hidden />
                    )}
                </div>
            </div>
        </Card>
    );
};

const lockedMapContainer = (sticky: boolean) => css`
    position: relative;
    width: 100%;
    height: ${DISTRICT_OSM_MAP_HEIGHT_COMPACT};

    @media (min-width: ${breakpoints.md}) {
        height: ${sticky ? "100%" : DISTRICT_OSM_MAP_HEIGHT_COMPACT};
    }
`;

const mapWrapper = css`
    width: 100%;
    height: 100%;
`;

const mapWrapperLocked = css`
    opacity: 0.75;
    filter: blur(0.2rem);
    user-select: none;
    pointer-events: none;
`;

const backButtonStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    padding: "0.375rem 0.75rem",
    border: "1px solid var(--colors-gray-300, #d1d5db)",
    borderRadius: "6px",
    background: "#fff",
    color: "var(--colors-secondary, #e63600)",
    fontSize: "0.875rem",
    lineHeight: 1.4,
    cursor: "pointer",
    "&:hover": {
        background: "var(--colors-gray-100, #f3f4f6)"
    }
});
